import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Service to check the current environment
 * Can be injected into components to conditionally show/hide elements based on environment
 */
@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  private readonly runtimeConfig = inject(RuntimeConfigService);

  /**
   * Combined gate for the `[CrossTabSync]…` debug log surface across the
   * app (CrossTabSyncService, FilingFormStateService, and the per-role
   * `remote-validation-triggered` subscribers).
   *
   * Returns `true` only when:
   *   - the build is non-production (build-time check that lets the
   *     minifier dead-code-eliminate the entire branch in prod), AND
   *   - the runtime `crossTabSyncDebug` flag is `true` (lets ops flip
   *     visibility per-deployment via `/etc/mbrs/frontend-overrides.json`
   *     without a fresh build).
   */
  isCrossTabSyncDebugEnabled(): boolean {
    return !environment.production && this.runtimeConfig.crossTabSyncDebug();
  }

  /**
   * Check if the current environment is development
   */
  isDevelopment(): boolean {
    return environment.env === 'development';
  }

  /**
   * Check if the current environment is staging
   */
  isStaging(): boolean {
    return environment.env === 'staging';
  }

  /**
   * Check if the current environment is production
   */
  isProduction(): boolean {
    return environment.production === true;
  }

  /**
   * Get the current environment name
   */
  getEnvironment(): string {
    return environment.env;
  }

  /**
   * Check if the current environment is NOT production (development or staging)
   */
  isNonProduction(): boolean {
    return !environment.production;
  }
}
